import { useEffect, useRef, useState } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "I was struggling for weeks until I reached out to Anant. His positivity and mental conditioning kept me going, and his detailed CV feedback made me realise I was in the right hands. The mock interviews transformed how I articulated my answers, and within weeks I had two job offers.",
    name: "Varun S",
    role: "Senior Project Manager",
    outcome: "2 offers in 3 weeks",
  },
  {
    quote:
      "After months of job hunting without success, Anant helped me refine my CV, improve my communication, and create a clear job search plan. His interview coaching completely changed my approach, and I landed a role perfectly suited to my skills.",
    name: "Supreet S",
    role: "Career Switcher",
    outcome: "Right role, right fit",
  },
  {
    quote:
      "Anant played a key role in helping me land my first job in Australia. He expertly translated my skills into a market-ready CV and optimised my job search strategy. The results spoke for themselves and I secured the role I was aiming for.",
    name: "Ankit B",
    role: "Project Manager",
    outcome: "First Australian role secured",
  },
  {
    quote:
      "Going from a Tester role to a Business Analyst role felt impossible after countless unsuccessful applications. Working with Anant gave me a structured plan and the confidence to reposition my experience. The transition happened in just 4 weeks.",
    name: "Anshul M",
    role: "From Tester to BA",
    outcome: "Successful career transition",
  },
  {
    quote:
      "I'd been searching for months when I finally reached out. Anant didn't just polish my CV — he helped me understand which roles I should actually be applying for. That clarity made all the difference.",
    name: "Kiran S",
    role: "Senior Business Analyst",
    outcome: "Dream role in 4 weeks",
  },
];

const ROTATE_MS = 5000;

export const TestimonialsCarousel = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % TESTIMONIALS.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const goTo = (i) => setIdx((i + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section
      data-testid="testimonials-carousel"
      className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
          Raving reviews
        </p>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 font-display">
          From clients who stopped guessing
          <span className="text-amber-600"> and started landing.</span>
        </h2>
      </div>

      <div className="mt-10 relative rounded-2xl border border-slate-200 bg-white p-8 lg:p-12 overflow-hidden min-h-[320px]">
        <Quote
          size={56}
          className="absolute top-6 right-8 text-amber-100"
          strokeWidth={2.5}
        />

        <div className="relative" style={{ minHeight: "180px" }}>
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              data-testid={`testimonial-slide-${i}`}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                i === idx
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 translate-y-4 pointer-events-none"
              }`}
              aria-hidden={i !== idx}
            >
              <p className="font-display text-xl sm:text-2xl text-slate-900 leading-relaxed font-medium">
                "{t.quote}"
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 text-white inline-flex items-center justify-center font-display font-bold">
                  {t.name.split(" ").map((s) => s[0]).join("")}
                </div>
                <div>
                  <p className="font-bold text-slate-900">{t.name}</p>
                  <p className="text-sm text-slate-500">{t.role}</p>
                </div>
                <div className="ml-auto hidden sm:block">
                  <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold px-3 py-1.5">
                    ✓ {t.outcome}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            {TESTIMONIALS.map((t, i) => (
              <button
                key={t.name}
                aria-label={`Show review ${i + 1}`}
                onClick={() => goTo(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx
                    ? "w-8 bg-slate-900"
                    : "w-1.5 bg-slate-300 hover:bg-slate-500"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(idx - 1)}
              data-testid="testimonial-prev"
              className="h-9 w-9 rounded-full border border-slate-300 bg-white inline-flex items-center justify-center text-slate-700 hover:border-slate-900 hover:text-slate-900"
              aria-label="Previous review"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => goTo(idx + 1)}
              data-testid="testimonial-next"
              className="h-9 w-9 rounded-full border border-slate-300 bg-white inline-flex items-center justify-center text-slate-700 hover:border-slate-900 hover:text-slate-900"
              aria-label="Next review"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500 text-center">
        Hover over a review to pause · {TESTIMONIALS.length} stories from
        successfully placed clients
      </p>
    </section>
  );
};

export default TestimonialsCarousel;
